"use client";

import { getTopAddresses, WalletSymbol } from "@/lib/getWalletAddresses";
/*
  文件说明（便于 AI 和开发者快速理解）

  概览：
  - 组件：OnChainWalletActivityTable
  - 作用：在卡片内渲染一张横向可滚动的表格，展示链上钱包活动（Label、Balance、24H Change、Last、Tag）。
  - 技术栈：React + TypeScript（客户端组件），MUI（Box, Table 系列），使用 ResizeObserver、pointer events 和 MUI 主题色。

  样式与布局要点：
  - 外层卡片（root）使用 `Box`，样式基于 theme.palette，带圆角、内边距和边框，overflow: hidden，支持通过 `containerHeight` 实现左右表格等高。
  - 表头为不透明、sticky（position: sticky; top: 0），使用 theme.palette.background.paper 作为背景色并且有分割线（theme.palette.divider）。
  - 滚动容器（scrollRef 指向）：
    - 永远隐藏纵向滚动（overflow-y: hidden），始终允许横向滚动（overflow-x: auto）。
    - 当表格列超出宽度时显示细横向滚动条（WebKit/Firefox 样式已定义）。
    - 禁用文本选择（user-select: none），以改善拖拽体验，防止在拖动时选中文本。

  功能与交互：
  - 指针拖拽（pointer events）实现：onPointerDown / onPointerMove / onPointerUp，用来拖动 scrollRef 的 scrollLeft，从而实现拖拽水平滚动（支持鼠标和触摸）。
  - 遮罩（masks）：
    - 遮罩不会放在滚动容器内部，而是渲染在外层 root 上。
    - 通过 root.getBoundingClientRect() 与 scroll.getBoundingClientRect() 计算 `maskRect`，并据此在 root 上绝对定位左右渐变遮罩，使遮罩在视觉上固定在卡片边缘（不会随内容滚动）。
    - showLeftMask / showRightMask 基于 scrollLeft 判断是否需要显示遮罩（左右是否还有被裁剪的内容）。
  - ResizeObserver：同时监听滚动容器和表格本身的尺寸变化以更新遮罩位置与 overflow 状态；也监听 window.resize。

  数据与行为变更（历史/当前重要修改）：
  - 表格固定可视行数：组件现在保证始终显示 EXACTLY 10 行（VISIBLE_ROWS = 10）。
    - 如果数据少于 10 行，会渲染占位空行以保证视觉高度一致。
    - 如果数据超过 10 行，仅展示前 10 行（可按需改为分页或虚拟滚动）。
  - 禁用垂直滚动、始终显示横向滚动条（以及拖拽手势），并在滚动区域设置了 user-select: none 以便拖拽时不选中文字。

  代码阅读提示（对 AI/开发者友好）：
  - 关键 ref：rootRef（根卡片），scrollRef（实际可横向滚动的容器）。
  - 关键状态：maskRect（{top,left,height,width,scrollLeft}），showLeftMask/showRightMask（布尔），hasOverflow（是否横向溢出）。
  - pointer-drag 的起点保存于 startX、startLeft，dragging 为 ref，isDragging 用于触发样式变化（如需要）。
  - 如果你要调整遮罩：修改计算 maskRect 的逻辑或遮罩宽度（当前 48px）；若遮罩被剪裁，检查父容器 overflow 与定位（root 必须为定位父）。
  - 如果需要支持更多行或分页：替换当前 VISIBLE_ROWS 行截断逻辑为分页或虚拟化实现（react-window / react-virtualized）。

  调试建议：
  - 在浏览器中交互时观察 console（组件在 dev 环境会输出简单 debug 信息）。
  - 若遮罩位置异常，打印 rootRect 与 scrollRect 并检查是否有祖先元素改变了布局（例如 transform、overflow、position 等）。
*/

import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography, Chip } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useMemo, useRef, useEffect, useState } from "react";
import { useTranslations } from "next-intl";

// 数据类型
type WalletRow = {
  label: string;
  balance: string;
  changePct: number; // e.g., +1.0 or -2.001
  last: string;
  tag?: string;
};

export interface OnChainWalletActivityTableProps {
  symbol: WalletSymbol;
  containerHeight?: string | number;
}

export default function OnChainWalletActivityTable({ symbol, containerHeight }: OnChainWalletActivityTableProps) {
  const t = useTranslations("Analytics");

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const dragging = useRef(false);
  const startX = useRef(0);
  const startLeft = useRef(0);
  const [data, setData] = useState<WalletRow[]>([]);
  const [hasSub, setHasSub] = useState<boolean | undefined>(undefined);
  const [showLeftMask, setShowLeftMask] = useState(false);
  const [showRightMask, setShowRightMask] = useState(false);
  const [maskRect, setMaskRect] = useState<{ top: number; left: number; height: number; scrollLeft: number } | null>(
    null
  );
  const [bottomMask, setBottomMask] = useState<{
    top: number;
    left: number;
    height: number;
    width: number;
  } | null>(null);

  const columns = useMemo(
    () => [
      { key: "label", labelKey: "OnChainWalletActivity.columns.label", minWidth: 160 },
      { key: "balance", labelKey: "OnChainWalletActivity.columns.balance", minWidth: 80, align: "right" as const },
      { key: "changePct", labelKey: "OnChainWalletActivity.columns.change24H", minWidth: 100, align: "right" as const },
      { key: "last", labelKey: "OnChainWalletActivity.columns.last", minWidth: 220 },
      { key: "tag", labelKey: "OnChainWalletActivity.columns.tag", minWidth: 90 },
    ],
    []
  );

  // Always show exactly 10 visible rows in the table body. If data has fewer rows,
  // append empty placeholder rows; if more, show the first 10.
  const VISIBLE_ROWS = 10;
  const displayRows = data.slice(0, VISIBLE_ROWS);
  const emptyRowCount = Math.max(0, VISIBLE_ROWS - displayRows.length);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    // update left/right masks based on scroll position
    const updateMasks = () => {
      const left = el.scrollLeft > 4;
      const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 4;
      setShowLeftMask(left);
      setShowRightMask(right);
    };

    const check = () => updateMasks();

    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    window.addEventListener("resize", check);
    // also observe content changes by observing table
    const table = el.querySelector("table");
    if (table) ro.observe(table);

    el.addEventListener("scroll", updateMasks);
    const updateRect = () => {
      const root = rootRef.current;
      const sr = scrollRef.current;
      if (!root || !sr) return;
      const rootRect = root.getBoundingClientRect();
      const scrollRect = sr.getBoundingClientRect();
      setMaskRect({
        top: scrollRect.top - rootRect.top,
        left: scrollRect.left - rootRect.left,
        height: scrollRect.height,
        scrollLeft: sr.scrollLeft,
      });
    };
    updateRect();
    const ro2 = new ResizeObserver(updateRect);
    ro2.observe(el);
    ro2.observe(document.body);
    window.addEventListener("resize", updateRect);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", check);
      el.removeEventListener("scroll", updateMasks);
      try {
        ro2.disconnect();
      } catch {}
      window.removeEventListener("resize", updateRect);
    };
  }, [data]);

  // compute bottomMask to cover last 6 rows when no subscription
  useEffect(() => {
    if (hasSub !== false || !maskRect) {
      setBottomMask(null);
      return;
    }
    const el = scrollRef.current;
    const table = el?.querySelector("table") as HTMLTableElement | null;
    if (!table) return setBottomMask(null);
    const tbody = table.querySelector("tbody") as HTMLTableSectionElement | null;
    if (!tbody) return setBottomMask(null);

    const rows = Array.from(tbody.querySelectorAll("tr")) as HTMLElement[];
    if (!rows.length) return setBottomMask(null);

    const dataRows = rows.filter((r) => !(r.id && String(r.id).startsWith("empty-")));
    const targetRows = dataRows.length ? dataRows : rows;
    const lastN = 6;
    const lastRows = targetRows.slice(-lastN);
    const firstOfLast = lastRows[0];
    const lastRow = lastRows[lastRows.length - 1];
    if (!firstOfLast || !lastRow) return setBottomMask(null);

    const root = rootRef.current;
    if (!root) return setBottomMask(null);
    const rootRect = root.getBoundingClientRect();
    const firstRect = firstOfLast.getBoundingClientRect();
    const lastRect = lastRow.getBoundingClientRect();

    const top = Math.max(0, firstRect.top - rootRect.top);
    // extend mask to cover from firstOfLast top down to the bottom of the root box
    const height = Math.max(0, rootRect.bottom - firstRect.top);
    const left = maskRect.left;
    const width = scrollRef.current ? scrollRef.current.getBoundingClientRect().width : 0;

    if (height < 8) return setBottomMask(null);
    setBottomMask({ top, left, height, width });
  }, [maskRect, hasSub, data]);

  async function fetchData() {
    const { rows: entities, hasSub } = await getTopAddresses(symbol);
    // Transform entities to WalletRow[]
    function formatBalance(val: any) {
      if (val == null) return "-";
      const n = typeof val === "number" ? Math.floor(val) : Math.floor(Number(val) || 0);
      return n.toLocaleString();
    }
    const rows: WalletRow[] = entities.map((e: any) => ({
      label: e.name || e.entityId,
      balance: `${formatBalance(e.balance)} U`,
      changePct: e.change24H,
      last: t("OnChainWalletActivity.last.na"), // Placeholder, replace with actual last transaction info
      tag: e.tag,
    }));
    // replace last with parsed latestTransfer if available
    const rowsWithLast = rows.map((r, i) => {
      const e = entities[i];
      const lastTransfer = e.latestTransfer;
      if (!lastTransfer) return r;
      const shortDisplay = (name?: string, addr?: string) => {
        if (name) return name;
        if (!addr) return "";
        return "0x" + addr.slice(-4);
      };
      const fmtDate = (dStr?: string) => {
        try {
          const d = new Date(dStr || "");
          const mm = String(d.getMonth() + 1).padStart(2, "0");
          const dd = String(d.getDate()).padStart(2, "0");
          const hh = String(d.getHours()).padStart(2, "0");
          const min = String(d.getMinutes()).padStart(2, "0");
          return `${mm}-${dd} ${hh}:${min}`;
        } catch {
          return "";
        }
      };
      const direction = lastTransfer.isSender
        ? t("OnChainWalletActivity.direction.sent")
        : t("OnChainWalletActivity.direction.received");
      const name = lastTransfer.isSender ? lastTransfer.toName : lastTransfer.fromName;
      const addr = lastTransfer.isSender ? lastTransfer.toAddress : lastTransfer.fromAddress;
      const display = shortDisplay(name, addr);
      const dateStr = fmtDate(lastTransfer.date || lastTransfer.createdAt || lastTransfer.timestamp);
      return { ...r, last: `${direction} ${display ? display : ""} (${dateStr})` };
    });
    setData(rowsWithLast);
    setHasSub(typeof hasSub === "boolean" ? hasSub : undefined);
  }

  // pointer drag handlers
  const onPointerDown = (e: React.PointerEvent) => {
    if (hasSub === false) return;
    const el = scrollRef.current;
    if (!el) return;
    dragging.current = true;
    startX.current = e.clientX;
    startLeft.current = el.scrollLeft;
    (e.target as Element).setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (hasSub === false) return;
    const el = scrollRef.current;
    if (!el || !dragging.current) return;
    const dx = e.clientX - startX.current;
    el.scrollLeft = startLeft.current - dx;
  };
  const onPointerUp = (e: React.PointerEvent) => {
    dragging.current = false;
    try {
      (e.target as Element).releasePointerCapture?.(e.pointerId);
    } catch {}
  };

  return (
    <Box
      ref={rootRef}
      sx={{
        flex: 1,
        position: "relative",
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.paperMedium",
        borderRadius: "0.75rem",
        pt: 1,
        overflow: "hidden",
        height: containerHeight,
        width: "100%",
        maxWidth: "100%",
      }}>
      <Typography
        variant="subtitle1"
        sx={{ fontSize: "var(--font-size-s)", fontWeight: 600, px: "1rem", py: "0.25rem" }}>
        {t("OnChainWalletActivity.title")}
      </Typography>
      <Box
        ref={scrollRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onWheel={(e: React.WheelEvent) => {
          // prevent horizontal wheel scrolling when user has no subscription
          if (hasSub === false && Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
        sx={(theme) => ({
          mt: "1rem",
          position: "relative",
          minHeight: 0,
          // Always hide vertical scroll and allow horizontal scroll
          overflowY: "hidden",
          overflowX: hasSub === false ? "hidden" : "auto",
          WebkitOverflowScrolling: "touch",
          // Prevent text selection while dragging
          userSelect: "none",
          width: "100%",
          maxWidth: "100%",
          // Show a thin horizontal scrollbar
          "&::-webkit-scrollbar": { height: 8 },
          "&::-webkit-scrollbar-track": {
            backgroundColor: alpha(theme.palette.background.paper, 0.6),
            borderRadius: 8,
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: alpha(theme.palette.text.primary, 0.28),
            borderRadius: 8,
            border: `2px solid transparent`,
            backgroundClip: "content-box",
          },
          scrollbarWidth: "thin",
          scrollbarColor: `${alpha(theme.palette.text.primary, 0.28)} ${alpha(theme.palette.background.paper, 0.6)}`,
        })}>
        <Table size="small" stickyHeader sx={{ minWidth: { xs: 760, md: 700 } }}>
          <TableHead>
            <TableRow>
              {columns.map((c) => (
                <TableCell
                  key={c.key}
                  align="left"
                  sx={(theme) => ({
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                    minWidth: c.minWidth,
                    px: "0.75rem",
                    py: "0.5rem",
                    borderTop: `1px solid ${theme.palette.divider}`,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    fontSize: "var(--font-size-xs)",
                    fontWeight: 400,
                    color: "text.secondary",
                    bgcolor: "background.paperMedium",
                    textWrap: "nowrap",
                    textAlign: "left",
                  })}>
                  {t(c.labelKey as any)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody sx={{ "& .MuiTableCell-root": { borderBottom: "none", textAlign: "left" } }}>
            {displayRows.map((r, idx) => (
              <TableRow key={idx} hover sx={{ "& .MuiTableCell-root": { borderBottom: "none" } }}>
                <TableCell
                  sx={{ p: "0.75rem", fontSize: "var(--font-size-xs)", whiteSpace: "nowrap", textAlign: "left" }}>
                  {r.label}
                </TableCell>
                <TableCell
                  sx={{ p: "0.75rem", fontSize: "var(--font-size-xs)", whiteSpace: "nowrap", textAlign: "left" }}>
                  {r.balance}
                </TableCell>
                <TableCell
                  sx={(theme) => ({
                    p: "0.75rem",
                    color: r.changePct >= 0 ? theme.palette.success.main : theme.palette.error.main,
                    fontSize: "var(--font-size-xs)",
                    fontVariantNumeric: "tabular-nums",
                    textAlign: "left",
                  })}>
                  {r.changePct >= 0 ? "+" : ""}
                  {r.changePct.toFixed(3).replace(/\.0{2,}$/, ".0")}%
                </TableCell>
                <TableCell sx={{ p: "0.75rem", maxWidth: 260, textAlign: "left" }}>
                  <Typography noWrap title={r.last} sx={{ opacity: 0.9, fontSize: "var(--font-size-xs)" }}>
                    {r.last}
                  </Typography>
                </TableCell>
                <TableCell sx={{ p: "0.75rem", textAlign: "left" }}>
                  <Typography variant="body1" sx={{ fontSize: "var(--font-size-xs)", opacity: 0.7 }}>
                    {r.tag}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}

            {Array.from({ length: emptyRowCount }).map((_, i) => (
              <TableRow key={`empty-${i}`} sx={{ "& .MuiTableCell-root": { borderBottom: "none" } }}>
                <TableCell sx={{ height: 40, textAlign: "left" }} />
                <TableCell sx={{ textAlign: "left" }} />
                <TableCell sx={{ textAlign: "left" }} />
                <TableCell sx={{ textAlign: "left" }} />
                <TableCell sx={{ textAlign: "left" }} />
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
      {/* 把遮罩渲染在外层卡片上，使用掩码矩形定位，保证遮罩不会随内容左右移动 */}
      {maskRect && showLeftMask && (
        <Box
          sx={(theme) => ({
            position: "absolute",
            pointerEvents: "none",
            zIndex: 40,
            transition: "opacity 180ms ease",
            // use theme background so mask matches light/dark mode
            background: `linear-gradient(to right, ${alpha((theme as any).palette.background.paperMedium, 1)}, ${alpha(
              theme.palette.background.paper,
              0
            )})`,
          })}
          style={{ top: `${maskRect.top}px`, left: `${maskRect.left}px`, height: `${maskRect.height}px`, width: 48 }}
        />
      )}
      {maskRect && showRightMask && (
        <Box
          sx={(theme) => ({
            position: "absolute",
            pointerEvents: "none",
            zIndex: 40,
            transition: "opacity 180ms ease",
            // use theme background so mask matches light/dark mode
            background: `linear-gradient(to left, ${alpha((theme as any).palette.background.paperMedium, 1)}, ${alpha(
              (theme as any).palette.background.paperMedium,
              0
            )})`,
          })}
          style={{
            top: `${maskRect.top}px`,
            left: `${
              maskRect.left +
              (maskRect && maskRect.left >= 0
                ? scrollRef.current
                  ? scrollRef.current.getBoundingClientRect().width - 48
                  : 0
                : 0)
            }px`,
            height: `${maskRect.height}px`,
            width: 48,
          }}
        />
      )}
      {bottomMask && (
        <Box
          sx={(theme) => ({
            position: "absolute",
            pointerEvents: "auto",
            zIndex: 45,
            transition: "opacity 180ms ease",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            // use theme background to match light/dark mode; fade to transparent at top
            background: `linear-gradient(180deg, rgba(0,0,0,0), ${alpha(
              (theme as any).palette.background.paper,
              0.88
            )})`,
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
            px: 2,
          })}
          style={{
            top: `${bottomMask.top}px`,
            left: `${bottomMask.left}px`,
            height: `${bottomMask.height}px`,
            width: bottomMask.width,
          }}>
          <Typography
            variant="caption"
            sx={(theme) => ({
              color: theme.palette.text.primary,
              textAlign: "center",
              maxWidth: "28.2rem",
              fontSize: "var(--font-size-xs)",
              lineHeight: 1.6,
            })}>
            {t("OnChainWalletActivity.mask.hint")}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
